import { useMemo, createContext, useCallback, useContext, useState } from 'react';
import { ContextData, ContextProviderProps } from 'types/context';
import { ListRules } from '../api/rules-endpoint';
import { RuleIdentifier } from '../types/rule';

const RulesContext = createContext<ContextData<RuleIdentifier> | undefined>(undefined);
RulesContext.displayName = 'RulesContext';

function useRulesContextData(): ContextData<RuleIdentifier> {
  const [rules, setRules] = useState<Array<RuleIdentifier>>([]);
  const [filteredRules, setFilteredRules] = useState<RuleIdentifier[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const fetchRules = useCallback(async () => {
    setIsLoading(true);
    const listRulesResponse = await ListRules();
    if (listRulesResponse.success && listRulesResponse.data) {
      setRules(listRulesResponse.data);
    } else {
      setRules([]);
    }
    setIsLoading(false);
  }, [setRules]);

  const filterRules = useCallback(
    async (filter: string) => {
      if (filter === '') {
        setFilteredRules(undefined);
        return;
      }

      const filterResult = rules.filter((job) => {
        return job.Name?.toLowerCase().includes(filter.toLowerCase()) || job.LongDescription?.toLowerCase().includes(filter.toLowerCase()) || job.ShortDescription?.toLowerCase().includes(filter.toLowerCase());
      });

      setFilteredRules(filterResult);
    },
    [setFilteredRules, rules],
  );

  return useMemo(
    () => ({
      data: filteredRules ?? rules,
      isLoading,
      fetchData: fetchRules,
      filterData: filterRules,
    }),
    [rules, filteredRules, isLoading, fetchRules, filterRules],
  );
}

export const useRulesContext = (): ContextData<RuleIdentifier> => {
  const rulesContext = useContext(RulesContext);
  if (!rulesContext) {
    throw new Error('useRulesContext must be used within the RulesContext.Provider');
  }
  return rulesContext;
};

export function RulesContextProvider({ children }: ContextProviderProps): JSX.Element {
  const ruleContextData = useRulesContextData();
  return <RulesContext.Provider value={ruleContextData}>{children}</RulesContext.Provider>;
}

export default { useRulesContext, RulesContextProvider };
